import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import { MallProvider, useMall } from "@/context/MallContext";
import { CartProvider } from "@/context/CartContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { SiteModeProvider, useSiteMode } from "@/context/SiteModeContext";
import { ROUTES, shown } from "@/siteMap";
import SimpleHome from "@/components/simple/SimpleHome";
import SimpleProduct from "@/pages/simple/SimpleProduct";
import PrintProductPage from "@/print/PrintProductPage";
import PrintLanding from "@/print/PrintLanding";
import UnknownShop from "@/pages/simple/UnknownShop";
import Layout from "@/components/layout/Layout";

import Home from "@/vault/directory/Home";
import SimpleWhitelabel from "@/pages/simple/SimpleWhitelabel";
import SignupShop from "@/pages/signup/SignupShop";
import SiteEntry, { SiteExit } from "@/pages/SiteEntry";
import ShirtsLanding from "@/pages/ShirtsLanding";
import Shirts from "@/pages/Shirts";
import DtfPage from "@/pages/DtfPage";
import ShirtProduct from "@/pages/ShirtProduct";
import GangSheet from "@/pages/GangSheet";
import Signs from "@/pages/Signs";
import SignCategory from "@/pages/SignCategory";
import SignProduct from "@/pages/SignProduct";
import Checkout from "@/pages/Checkout";
import Bags from "@/pages/Bags";
import OrderConfirmation from "@/pages/OrderConfirmation";
import TrackOrder from "@/pages/TrackOrder";
import Pay from "@/pages/Pay";
import Reorder from "@/pages/Reorder";
import ArtworkRequest from "@/pages/ArtworkRequest";
import Wholesale from "@/pages/Wholesale";
import Classes from "@/vault/classes/Classes";
import WebsiteDesign from "@/vault/legacy_extras/WebsiteDesign";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Account from "@/pages/Account";
import Purchases from "@/pages/Purchases";
import Payouts from "@/pages/Payouts";
import GangSheets from "@/pages/GangSheets";
import ServiceRequestsPage from "@/vault/service_requests/ServiceRequestsPage";
import EmailGuidePage from "@/pages/EmailGuidePage";
import Admin from "@/pages/Admin";
import Proof from "@/pages/Proof";
import AuthCallback from "@/pages/AuthCallback";
import Marketplace from "@/vault/marketplace/Marketplace";
import Listing from "@/vault/marketplace/Listing";
import SellListing from "@/vault/marketplace/SellListing";
import MyListings from "@/vault/marketplace/MyListings";
import MachineServices from "@/vault/legacy_extras/MachineServices";
import StorePage from "@/pages/StorePage";
import StorePolicies from "@/pages/StorePolicies";
import OpenSpace from "@/vault/marketplace/OpenSpace";
import VendorOrders from "@/pages/VendorOrders";
import StoreProducts from "@/pages/StoreProducts";
import Promotions from "@/pages/Promotions";
import Analytics from "@/pages/Analytics";
import DropCards from "@/vault/legacy_extras/DropCards";
import Reviews from "@/pages/Reviews";
import PayoutHistory from "@/pages/PayoutHistory";
import StoreBillboards from "@/vault/billboards/StoreBillboards";
import StoreInbox from "@/pages/StoreInbox";
import StoreDomain from "@/pages/StoreDomain";
import ConsignmentMode from "@/vault/legacy_extras/ConsignmentMode";
import StoreClasses from "@/vault/classes/StoreClasses";
import FoodCourt from "@/vault/community/FoodCourt";
import Spaces from "@/vault/spaces/Spaces";
import VendorTicket from "@/pages/VendorTicket";
import VendorSetup from "@/pages/VendorSetup";
import StoreHomepage from "@/vault/homepage_slots/StoreHomepage";
import PaymentResult from "@/pages/PaymentResult";

function Protected({ children, admin }) {
    const { user, ready } = useAuth();
    if (!ready) return <div className="flex min-h-screen items-center justify-center bg-brand-ink text-zinc-400">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (admin && user.role !== "admin") return <Navigate to="/account" replace />;
    return children;
}

function MallRedirect() {
    const { slug } = useParams();
    return <Navigate to={`/stores/${slug}`} replace />;
}

/** The old racks and shelves all live under Shirts now. */
const OLD_SHELF = { "same-day": "/shirts/white", standard: "/shirts/colored" };
function ShelfRedirect() {
    const { bucket } = useParams();
    return <Navigate to={OLD_SHELF[bucket] || "/shirts"} replace />;
}

/**
 * `/` is the shop's home page (Phase 10): the house shop on 13thav.com, a tenant's shop on its own host.
 * The white-label pitch lives at /whitelabel. An unknown host gets the "isn't open yet" screen.
 */
function FrontDoor() {
    const { demo, soloByHost, site, unknownHost } = useMall();
    const { shop } = useSiteMode();
    if (unknownHost) return <UnknownShop />;
    if ((demo || soloByHost) && site?.store && shop.features?.directory) return <Home />;
    return <Layout title="Order"><SimpleHome store={(demo || soloByHost) ? site?.store : null} /></Layout>;
}

/** Every route reads the map. Storefront rows only hide on a false flag; back-office rows still follow Simple | Classic. */
function Gate({ path, children }) {
    const ctx = useSiteMode();
    if (!ctx.ready) return null;
    const backOffice = path.startsWith("/account") || path.startsWith("/admin");
    return shown(ROUTES[path], backOffice ? ctx : ctx.shop) ? children : <Navigate to="/" replace />;
}

/** Phase 4: the consignment page CONSOLIDATED into the per-product switch on Products; the old screen waits in the vault. */
function ConsignmentOrProducts() {
    const { features } = useSiteMode();
    return features?.legacy_extras ? <Protected><ConsignmentMode /></Protected> : <Navigate to="/account/products" replace />;
}

function AppRoutes() {
    const location = useLocation();
    // Google OAuth returns to {origin}/account#session_id=... — handle it before any route renders.
    if (location.hash?.includes("session_id=")) return <AuthCallback />;
    return (
        <Routes>
            <Route path="/" element={<Gate path="/"><FrontDoor /></Gate>} />
            <Route path="/whitelabel" element={<Gate path="/whitelabel"><SimpleWhitelabel /></Gate>} />
            <Route path="/signup/shop" element={<Gate path="/signup/shop"><SignupShop /></Gate>} />
            <Route path="/site/exit" element={<Gate path="/site/exit"><SiteExit /></Gate>} />
            <Route path="/site/:slug" element={<Gate path="/site/:slug"><SiteEntry /></Gate>} />
            <Route path="/shop/:bucket" element={<Gate path="/shop/:bucket"><ShelfRedirect /></Gate>} />
            {/* SHIRTS: landing cards → garment grid → the original full-screen designer */}
            <Route path="/shirts" element={<Gate path="/shirts"><ShirtsLanding /></Gate>} />
            <Route path="/shirts/dtf" element={<Gate path="/shirts/dtf"><DtfPage /></Gate>} />
            <Route path="/shirts/dtf/gang-sheet" element={<Gate path="/shirts/dtf/gang-sheet"><GangSheet /></Gate>} />
            <Route path="/shirts/:kind" element={<Gate path="/shirts/:kind"><Shirts /></Gate>} />
            <Route path="/shirts/:kind/:slug" element={<Gate path="/shirts/:kind/:slug"><ShirtProduct /></Gate>} />
            {/* SIGNS: material cards → product cards → the original full-screen designer */}
            <Route path="/signs" element={<Gate path="/signs"><Signs /></Gate>} />
            <Route path="/signs/:category" element={<Gate path="/signs/:category"><SignCategory /></Gate>} />
            <Route path="/signs/:category/:slug" element={<Gate path="/signs/:category/:slug"><SignProduct /></Gate>} />
            <Route path="/order/white-tees" element={<Gate path="/order/white-tees"><Navigate to="/shirts/white" replace /></Gate>} />
            <Route path="/order/colored-apparel" element={<Gate path="/order/colored-apparel"><Navigate to="/shirts/colored" replace /></Gate>} />
            <Route path="/order/dtf-gang-sheet" element={<Gate path="/order/dtf-gang-sheet"><Navigate to="/shirts/dtf/gang-sheet" replace /></Gate>} />
            <Route path="/order/custom-sign" element={<Gate path="/order/custom-sign"><Navigate to="/signs" replace /></Gate>} />
            <Route path="/order/shirts" element={<Gate path="/order/shirts"><Navigate to="/shirts" replace /></Gate>} />
            <Route path="/order/signs" element={<Gate path="/order/signs"><Navigate to="/signs" replace /></Gate>} />
            <Route path="/order/gangsheet" element={<Gate path="/order/gangsheet"><Navigate to="/shirts/dtf/gang-sheet" replace /></Gate>} />
            {/* one-screen product page for the groups without a full-screen designer (transfers, custom cards) */}
            <Route path="/order/:slug" element={<Gate path="/order/:slug"><SimpleProduct /></Gate>} />
            <Route path="/print" element={<Gate path="/print"><PrintLanding /></Gate>} />
            <Route path="/print/:slug" element={<Gate path="/print/:slug"><PrintProductPage /></Gate>} />
            <Route path="/cart" element={<Gate path="/cart"><Bags /></Gate>} />
            <Route path="/checkout" element={<Gate path="/checkout"><Navigate to="/cart" replace /></Gate>} />
            <Route path="/checkout/:store" element={<Gate path="/checkout/:store"><Checkout /></Gate>} />
            <Route path="/order-confirmation/:id" element={<Gate path="/order-confirmation/:id"><OrderConfirmation /></Gate>} />
            <Route path="/track" element={<Gate path="/track"><TrackOrder /></Gate>} />
            <Route path="/track/:code" element={<Gate path="/track/:code"><TrackOrder /></Gate>} />
            <Route path="/pay" element={<Gate path="/pay"><Pay /></Gate>} />
            <Route path="/artwork/pay" element={<Gate path="/artwork/pay"><Pay /></Gate>} />
            <Route path="/r/:token" element={<Gate path="/r/:token"><Reorder /></Gate>} />
            <Route path="/proof/:token" element={<Gate path="/proof/:token"><Proof /></Gate>} />
            <Route path="/artwork-request" element={<Gate path="/artwork-request"><ArtworkRequest /></Gate>} />
            <Route path="/wholesale" element={<Gate path="/wholesale"><Wholesale /></Gate>} />
            <Route path="/classes" element={<Gate path="/classes"><Classes /></Gate>} />
            <Route path="/website-design" element={<Gate path="/website-design"><WebsiteDesign /></Gate>} />
            <Route path="/stores" element={<Gate path="/stores"><Navigate to="/directory" replace /></Gate>} />
            <Route path="/stores/:slug" element={<Gate path="/stores/:slug"><StorePage /></Gate>} />
            <Route path="/stores/:slug/policies" element={<Gate path="/stores/:slug/policies"><StorePolicies /></Gate>} />
            <Route path="/account/sell" element={<Gate path="/account/sell"><Protected><OpenSpace /></Protected></Gate>} />
            <Route path="/account/orders" element={<Gate path="/account/orders"><Protected><VendorOrders /></Protected></Gate>} />
            <Route path="/account/products" element={<Gate path="/account/products"><Protected><StoreProducts /></Protected></Gate>} />
            <Route path="/account/billboards" element={<Gate path="/account/billboards"><Protected><StoreBillboards /></Protected></Gate>} />
            <Route path="/account/coupons" element={<Gate path="/account/coupons"><Protected><Promotions /></Protected></Gate>} />
            <Route path="/account/consignment" element={<ConsignmentOrProducts />} />
            <Route path="/account/wizard" element={<Gate path="/account/wizard"><Protected><Account /></Protected></Gate>} />
            <Route path="/account/classes" element={<Gate path="/account/classes"><Protected><StoreClasses /></Protected></Gate>} />
            <Route path="/community" element={<Gate path="/community"><FoodCourt /></Gate>} />
            <Route path="/food-court" element={<Gate path="/food-court"><Navigate to="/community" replace /></Gate>} />
            <Route path="/spaces" element={<Gate path="/spaces"><Spaces /></Gate>} />
            <Route path="/account/inbox" element={<Gate path="/account/inbox"><Protected><StoreInbox /></Protected></Gate>} />
            <Route path="/account/orders/:id/ticket" element={<Gate path="/account/orders/:id/ticket"><Protected><VendorTicket /></Protected></Gate>} />
            <Route path="/account/store" element={<Gate path="/account/store"><Protected><VendorSetup /></Protected></Gate>} />
            <Route path="/account/homepage" element={<Gate path="/account/homepage"><Protected><StoreHomepage /></Protected></Gate>} />
            <Route path="/account/domain" element={<Gate path="/account/domain"><Protected><StoreDomain /></Protected></Gate>} />
            <Route path="/directory" element={<Gate path="/directory"><Home /></Gate>} />
            <Route path="/mall" element={<Gate path="/mall"><Navigate to="/directory" replace /></Gate>} />
            <Route path="/mall/setup" element={<Gate path="/mall/setup"><Navigate to="/account/store" replace /></Gate>} />
            <Route path="/mall/:slug" element={<Gate path="/mall/:slug"><MallRedirect /></Gate>} />
            <Route path="/payment/success" element={<Gate path="/payment/success"><PaymentResult /></Gate>} />
            <Route path="/payment/cancel" element={<Gate path="/payment/cancel"><PaymentResult cancelled /></Gate>} />
            <Route path="/login" element={<Gate path="/login"><Login /></Gate>} />
            <Route path="/register" element={<Gate path="/register"><Register /></Gate>} />
            <Route path="/supplies" element={<Gate path="/supplies"><Marketplace /></Gate>} />
            <Route path="/supplies/sell" element={<Gate path="/supplies/sell"><Protected><SellListing /></Protected></Gate>} />
            <Route path="/supplies/:slug" element={<Gate path="/supplies/:slug"><Listing /></Gate>} />
            <Route path="/services" element={<Gate path="/services"><MachineServices /></Gate>} />
            <Route path="/account/listings" element={<Gate path="/account/listings"><Protected><MyListings /></Protected></Gate>} />
            <Route path="/account" element={<Gate path="/account"><Protected><Account /></Protected></Gate>} />
            <Route path="/account/purchases" element={<Gate path="/account/purchases"><Protected><Purchases /></Protected></Gate>} />
            <Route path="/account/payouts" element={<Gate path="/account/payouts"><Protected><Payouts /></Protected></Gate>} />
            <Route path="/account/gang-sheets" element={<Gate path="/account/gang-sheets"><Protected><GangSheets /></Protected></Gate>} />
            <Route path="/account/service-requests" element={<Gate path="/account/service-requests"><Protected admin><ServiceRequestsPage /></Protected></Gate>} />
            <Route path="/account/email-guide" element={<Gate path="/account/email-guide"><Protected><EmailGuidePage /></Protected></Gate>} />
            <Route path="/account/discounts" element={<Gate path="/account/discounts"><Protected><Promotions /></Protected></Gate>} />
            <Route path="/account/analytics" element={<Gate path="/account/analytics"><Protected><Analytics /></Protected></Gate>} />
            <Route path="/account/drop-cards" element={<Gate path="/account/drop-cards"><Protected><DropCards /></Protected></Gate>} />
            <Route path="/account/reviews" element={<Gate path="/account/reviews"><Protected><Reviews /></Protected></Gate>} />
            <Route path="/account/payout-history" element={<Gate path="/account/payout-history"><Protected><PayoutHistory /></Protected></Gate>} />
            <Route path="/admin" element={<Gate path="/admin"><Protected admin><Admin /></Protected></Gate>} />
            <Route path="*" element={<Gate path="*"><Navigate to="/" replace /></Gate>} />
        </Routes>
    );
}

function App() {
    return (
        <AuthProvider>
            <SettingsProvider>
                <CartProvider>
                    <MallProvider>
                    <BrowserRouter>
                    <SiteModeProvider>
                        <ScrollToTop />
                        <Toaster theme="dark" position="top-center" richColors />
                        <AppRoutes />
                    </SiteModeProvider>
                    </BrowserRouter>
                    </MallProvider>
                </CartProvider>
            </SettingsProvider>
        </AuthProvider>
    );
}

export default App;
